import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaKey,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaTimes,
  FaExclamationCircle,
  FaCheckCircle,
  FaUserCheck,
  FaUserTimes,
  FaSearch,
  FaFilter,
  FaShieldAlt,
  FaUser,
  FaDownload,
  FaUsers
} from 'react-icons/fa';

import './UserManagementPage.css';
import api from '../services/api';

/**
 * UserManagementPage Component
 * Bootstrap-aligned layout (no Tailwind utility classes)
 * Features: CRUD operations, search/filter, responsive design, accessibility support
 */
const UserManagementPage = ({ currentUser }) => {
  /* ==================== STATE MANAGEMENT ==================== */

  // Core data state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toggleAction, setToggleAction] = useState(null); // { userId, username, newStatus }

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Form data states
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'field',
    password: '',
    is_active: true
  });

  const [editUserData, setEditUserData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'field',
    is_active: true
  });

  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  // UI state for password visibility
  const [showNewPasswordInCreate, setShowNewPasswordInCreate] = useState(false);
  const [showNewPasswordInReset, setShowNewPasswordInReset] = useState(false);
  const [showConfirmPasswordInReset, setShowConfirmPasswordInReset] = useState(false);

  /* ==================== LIFECYCLE HOOKS ==================== */

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (users && !Array.isArray(users)) {
      // Defensive log
      console.error('Warning: users state is not an array:', users);
    }
  }, [users]);

  /* ==================== API FUNCTIONS ==================== */

  const fetchUsers = async () => {
    console.log('fetchUsers: Starting to fetch users');
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/auth/users');
      console.log('fetchUsers: API response received', response);

      // Handle different possible response structures:
      // - [...] OR { users: [...] } OR { data: [...] }
      let userData = [];

      if (Array.isArray(response.data)) {
        userData = response.data;
      } else if (response.data && Array.isArray(response.data.users)) {
        userData = response.data.users;
      } else if (response.data && Array.isArray(response.data.data)) {
        userData = response.data.data;
      } else {
        console.error('Unexpected API response structure:', response.data);

        const possibleArrayKeys = Object.keys(response.data || {}).filter((key) =>
          Array.isArray(response.data[key])
        );

        if (possibleArrayKeys.length > 0) {
          userData = response.data[possibleArrayKeys[0]];
          console.warn(`Using array from key '${possibleArrayKeys[0]}' in response`);
        } else {
          userData = [];
          setError('Invalid response format from server. Expected an array of users.');
        }
      }

      console.log('fetchUsers: Setting users', { count: userData.length, users: userData });
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      console.log('fetchUsers: Error fetching users', err);
      if (err.response) {
        setError(err.response.data?.error || `Failed to fetch users: ${err.response.status}`);
      } else if (err.request) {
        setError('Network error fetching users. Please check your connection or API server.');
      } else {
        setError(`Error fetching users: ${err.message}`);
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if the current user can edit or delete another user
   * Rules:
   * - Super admins (is_super_admin flag) can edit/delete anyone
   * - Regular Office Admins (role === 'admin') can edit/delete field techs only
   * - Office Admins cannot edit/delete other Office Admins (except super admin can)
   * - No one can edit/delete themselves
   */
  const canModifyUser = (targetUser) => {
    if (!currentUser || !targetUser) {
      console.log('canModifyUser: Missing currentUser or targetUser', { currentUser, targetUser });
      return false;
    }

    // Can't modify yourself
    if (currentUser.id === targetUser.id) return false;

    // Check if users are super admins using is_super_admin flag
    const isTargetSuperAdmin = targetUser.is_super_admin || false;
    const isCurrentSuperAdmin = currentUser.is_super_admin || false;

    // Super administrators can modify anyone
    if (isCurrentSuperAdmin) return true;

    // No one else can modify super administrators
    if (isTargetSuperAdmin) return false;

    const currentRole = (currentUser.role || '').toLowerCase();
    const targetRole = (targetUser.role || '').toLowerCase();

    // Office Admins cannot modify other Office Admins
    if (currentRole === 'admin' && targetRole === 'admin') {
      return false;
    }

    // Office Admins can modify non-admin users
    if (currentRole === 'admin' && targetRole !== 'admin') {
      console.log('canModifyUser: Office admin can modify non-admin user', {
        currentUserRole: currentRole,
        targetUserRole: targetRole,
        targetUsername: targetUser.username
      });
      return true;
    }

    console.log('canModifyUser: No permission - default false', {
      currentUserRole: currentRole,
      currentUsername: currentUser.username,
      targetUserRole: targetRole,
      targetUsername: targetUser.username
    });
    return false;
  };

  // Helper function to capitalize first letter of each word
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleCreateUser = async () => {
    if (
      !newUserData.username ||
      !newUserData.email ||
      !newUserData.password ||
      !newUserData.first_name ||
      !newUserData.last_name
    ) {
      setError('All fields (Username, Email, First Name, Last Name, Password) are required.');
      return;
    }

    if (newUserData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    try {
      await api.post('/auth/users', newUserData);

      setSuccess('User created successfully!');
      setShowCreateModal(false);
      resetNewUserForm();
      fetchUsers();
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || `Failed to create user: ${err.response.status}`);
      } else {
        setError('Network error creating user.');
      }
    }
  };

  const handleEditUser = async () => {
    if (!editUserData.username || !editUserData.email || !editUserData.first_name || !editUserData.last_name) {
      setError('Username, Email, First Name, and Last Name are required.');
      return;
    }

    // Check permission before editing
    if (selectedUser && !canModifyUser(selectedUser)) {
      setError('You do not have permission to edit this user.');
      return;
    }

    try {
      const payload = {
        username: editUserData.username,
        email: editUserData.email,
        first_name: editUserData.first_name,
        last_name: editUserData.last_name,
        role: editUserData.role,
        is_active: editUserData.is_active
      };

      await api.put(`/auth/users/${selectedUser.id}`, payload);

      setSuccess('User updated successfully!');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || `Failed to update user: ${err.response.status}`);
      } else {
        setError('Network error updating user.');
      }
    }
  };

  const handleDeleteUser = async (userId, username, targetUser) => {
    console.log('handleDeleteUser called', { userId, username, targetUser });

    if (userId === currentUser?.id) {
      console.log('handleDeleteUser: Cannot delete own account');
      setError("You cannot delete your own account.");
      return;
    }

    // Check permission before deleting
    const hasPermission = canModifyUser(targetUser);
    console.log('handleDeleteUser: Permission check result', { hasPermission });

    if (targetUser && !hasPermission) {
      console.log('handleDeleteUser: No permission to delete user');
      setError('You do not have permission to delete this user.');
      return;
    }

    // Emphasize permanent deletion
    if (!window.confirm(`⚠️ PERMANENT DELETION WARNING ⚠️\n\nAre you sure you want to PERMANENTLY delete user "${username}"?\n\nThis will:\n• Remove the user from the database completely\n• Delete all associated data\n• Cannot be undone or recovered\n\nIf you want to temporarily disable the user instead, use the "Deactivate" button.`)) {
      console.log('handleDeleteUser: User cancelled confirmation');
      return;
    }

    console.log('handleDeleteUser: About to call API delete');
    try {
      const response = await api.delete(`/auth/users/${userId}`);
      console.log('handleDeleteUser: API delete successful', response);
      setSuccess(`User "${username}" has been permanently deleted.`);
      fetchUsers();
    } catch (err) {
      console.log('handleDeleteUser: API delete failed', err);
      if (err.response) {
        console.log('handleDeleteUser: Error response', err.response);
        setError(err.response.data?.error || `Failed to delete user: ${err.response.status}`);
      } else {
        console.log('handleDeleteUser: Network error');
        setError('Network error deleting user.');
      }
    }
  };

  const handleToggleUserStatus = async (userId, username, currentStatus, targetUser) => {
    console.log('handleToggleUserStatus called', { userId, username, currentStatus, targetUser });

    if (userId === currentUser?.id) {
      console.log('handleToggleUserStatus: Cannot modify own account');
      setError("You cannot modify your own account status.");
      return;
    }

    // Check permission before modifying
    const hasPermission = canModifyUser(targetUser);
    console.log('handleToggleUserStatus: Permission check result', { hasPermission });

    if (targetUser && !hasPermission) {
      console.log('handleToggleUserStatus: No permission to modify user');
      setError('You do not have permission to modify this user.');
      return;
    }

    const newStatus = !currentStatus;

    // Show confirmation modal
    setToggleAction({ userId, username, newStatus });
    setShowToggleModal(true);
  };

  const confirmToggleUserStatus = async () => {
    if (!toggleAction) return;

    const { userId, username, newStatus } = toggleAction;
    const action = newStatus ? 'activate' : 'deactivate';
    const actionPast = newStatus ? 'activated' : 'deactivated';

    setShowToggleModal(false);
    console.log('confirmToggleUserStatus: About to call API');
    try {
      const response = await api.put(`/auth/users/${userId}/status`, {
        is_active: newStatus
      });
      console.log('confirmToggleUserStatus: API successful', response);
      setSuccess(`User "${username}" has been ${actionPast} successfully.`);
      fetchUsers();
    } catch (err) {
      console.log('confirmToggleUserStatus: API failed', err);
      if (err.response) {
        console.log('confirmToggleUserStatus: Error response', err.response);
        setError(err.response.data?.error || `Failed to ${action} user: ${err.response.status}`);
      } else {
        console.log('confirmToggleUserStatus: Network error');
        setError(`Network error ${action}ing user.`);
      }
    } finally {
      setToggleAction(null);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordData.new_password || !passwordData.confirm_password) {
      setError('Please enter and confirm the new password.');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    try {
      await api.post(`/auth/users/${selectedUser.id}/reset-password`, {
        new_password: passwordData.new_password
      });

      setSuccess(`Password reset successfully for ${selectedUser.username}.`);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || `Failed to reset password: ${err.response.status}`);
      } else {
        setError('Network error resetting password.');
      }
    }
  };

  /* ==================== UTILITY FUNCTIONS ==================== */

  const handleDownloadScript = () => {
    const pythonScript = `#!/usr/bin/env python3
"""
Excel Data Entry Automation Script for Mac
Reads the most recent Excel file from Downloads folder and automates data entry.

Format per line item:
Type First Line, Tab, Tab, Tab, Tab, Tab, Tab
Type Service, Tab, Line item Door name, Tab, Tab, Tab, Tab, Enter Total Cost, Tab
Type Service, Tab, --(Qty) Description, Tab, Tab, Tab, Tab, Tab

Requirements:
- pip install openpyxl pyautogui
"""

import os
import time
import glob
from pathlib import Path
import openpyxl
import pyautogui

pyautogui.PAUSE = 0.05

def get_most_recent_excel():
    downloads_path = Path.home() / "Downloads"
    excel_files = list(downloads_path.glob("*.xlsx")) + list(downloads_path.glob("*.xls"))

    if not excel_files:
        raise FileNotFoundError("No Excel files found in Downloads folder")

    most_recent = max(excel_files, key=lambda x: x.stat().st_mtime)
    return most_recent

def read_excel_data(file_path):
    wb = openpyxl.load_workbook(file_path)
    ws = wb.active
    data = []

    for row in ws.iter_rows(min_row=2, values_only=True):
        if not any(row):
            continue

        door_name = row[0] if len(row) > 0 else ""
        qty = row[1] if len(row) > 1 else ""
        description = row[2] if len(row) > 2 else ""
        total_cost = row[3] if len(row) > 3 else ""

        data.append({
            'door_name': str(door_name) if door_name else "",
            'qty': str(qty) if qty else "",
            'description': str(description) if description else "",
            'total_cost': str(total_cost) if total_cost else ""
        })

    return data

def type_with_tabs(text, num_tabs):
    pyautogui.write(text, interval=0.05)
    for _ in range(num_tabs):
        pyautogui.press('tab')
        time.sleep(0.1)

def process_line_item(item):
    print(f"Typing door name: {item['door_name']}")
    type_with_tabs(item['door_name'], 7)

    print(f"Typing line 2: Service, {item['door_name']}, {item['total_cost']}")
    type_with_tabs("Service", 1)
    type_with_tabs(item['door_name'], 4)
    type_with_tabs(item['total_cost'], 1)

    formatted_description = f"--({item['qty']}) {item['description']}"
    print(f"Typing line 3: Service, {formatted_description}")
    type_with_tabs("Service", 1)
    type_with_tabs(formatted_description, 6)

def main():
    print("=" * 60)
    print("Excel Data Entry Automation Script")
    print("=" * 60)

    try:
        excel_file = get_most_recent_excel()
        print(f"\\nFound Excel file: {excel_file.name}")

        data = read_excel_data(excel_file)
        print(f"Loaded {len(data)} line items from Excel")

        print("\\nPreview of data:")
        for i, item in enumerate(data[:3], 1):
            print(f"\\n  Item {i}:")
            print(f"    Door: {item['door_name']}")
            print(f"    Qty: {item['qty']}")
            print(f"    Description: {item['description']}")
            print(f"    Cost: {item['total_cost']}")

        if len(data) > 3:
            print(f"\\n  ... and {len(data) - 3} more items")

        print("\\n" + "=" * 60)
        print("READY TO START DATA ENTRY")
        print("Click on the target application/field now!")
        print("=" * 60)

        for i in range(10, 0, -1):
            print(f"Starting in {i} seconds...", end='\\r')
            time.sleep(1)

        print("\\nStarting data entry NOW!                    ")
        print("=" * 60)

        for i, item in enumerate(data, 1):
            print(f"\\nProcessing item {i}/{len(data)}")
            process_line_item(item)
            time.sleep(0.5)

        print("\\n" + "=" * 60)
        print("DATA ENTRY COMPLETE!")
        print("=" * 60)

    except FileNotFoundError as e:
        print(f"\\nError: {e}")
        print("Please make sure you have Excel files in your Downloads folder")
    except Exception as e:
        print(f"\\nError: {e}")
        import traceback
        traceback.print_exc()

if __name__ == "__main__":
    main()`;

    const blob = new Blob([pythonScript], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'excel_data_entry.py';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    setSuccess('Excel automation script downloaded! See instructions for setup.');
  };

  const resetNewUserForm = () => {
    setNewUserData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      role: 'field',
      password: '',
      is_active: true
    });
    setShowNewPasswordInCreate(false);
  };

  const openEditModal = (user) => {
    // Check permission before opening edit modal
    if (!canModifyUser(user)) {
      setError('You do not have permission to edit this user.');
      return;
    }

    setSelectedUser(user);
    setEditUserData({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      is_active: user.is_active
    });
    setError('');
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowNewPasswordInReset(false);
    setShowConfirmPasswordInReset(false);
    setError('');
    setShowPasswordModal(true);
  };

  const filteredUsers = Array.isArray(users)
    ? users.filter((user) => {
        const searchLower = searchTerm.toLowerCase();

        const matchesSearch =
          (user?.username?.toLowerCase() || '').includes(searchLower) ||
          (user?.email?.toLowerCase() || '').includes(searchLower) ||
          (user?.full_name?.toLowerCase() || '').includes(searchLower) ||
          (user?.first_name?.toLowerCase() || '').includes(searchLower) ||
          (user?.last_name?.toLowerCase() || '').includes(searchLower);

        const matchesRole = roleFilter === 'all' || user?.role === roleFilter;

        return matchesSearch && matchesRole;
      })
    : [];

  /* ==================== RENDER HELPERS ==================== */

  const renderAlerts = () => (
    <>
      {error && (
        <div className="mb-3 alert-message error-message p-3 rounded shadow-sm">
          <div className="d-flex align-items-start">
            <FaExclamationCircle className="me-2" style={{ width: 22, height: 22 }} />
            <div>
              <div className="fw-bold">Error</div>
              <div className="small">{error}</div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-3 alert-message success-message p-3 rounded shadow-sm">
          <div className="d-flex align-items-start">
            <FaCheckCircle className="me-2" style={{ width: 22, height: 22 }} />
            <div>
              <div className="fw-bold">Success</div>
              <div className="small">{success}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderUserRow = (user) => (
    <tr key={user.id}>
      <td>
        <div className="d-flex align-items-center gap-3">
          <div className="user-avatar-initials">
            {user.first_name?.charAt(0)}
            {user.last_name?.charAt(0)}
          </div>
          <div className="user-info-text">
            <div className="user-name">{user.full_name}</div>
            <div className="user-details">
              {user.username} • {user.email}
            </div>
          </div>
        </div>
      </td>
      <td>
        <span className={`role-badge ${user.role}`}>
          {user.role === 'admin' ? (
            <>
              <FaShieldAlt className="me-1" style={{ width: 14, height: 14 }} />
              Office Admin
            </>
          ) : (
            <>
              <FaUser className="me-1" style={{ width: 14, height: 14 }} />
              Field Tech
            </>
          )}
        </span>
      </td>
      <td>
        <div className={`status-indicator ${user.is_active ? 'active' : 'inactive'}`}>
          {user.is_active ? (
            <FaUserCheck style={{ width: 20, height: 20 }} />
          ) : (
            <FaUserTimes style={{ width: 20, height: 20 }} />
          )}
          <span>{user.is_active ? 'Active' : 'Inactive'}</span>
        </div>
      </td>
      <td className="text-muted">
        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
      </td>
      <td className="text-end">
        <div className="action-buttons">
          {canModifyUser(user) ? (
            <button
              type="button"
              onClick={() => openEditModal(user)}
              className="edit-button"
              title="Edit User"
              aria-label={`Edit user ${user.username}`}
            >
              <FaEdit style={{ width: 20, height: 20 }} />
            </button>
          ) : (
            <button
              type="button"
              className="edit-button"
              disabled
              title="No permission to edit this user"
              aria-label={`Cannot edit user ${user.username}`}
              style={{ opacity: 0.3, cursor: 'not-allowed' }}
            >
              <FaEdit style={{ width: 20, height: 20 }} />
            </button>
          )}

          <button
            type="button"
            onClick={() => openPasswordModal(user)}
            className="password-button"
            title="Reset Password"
            aria-label={`Reset password for ${user.username}`}
          >
            <FaKey style={{ width: 20, height: 20 }} />
          </button>

          {canModifyUser(user) ? (
            <button
              type="button"
              onClick={() => handleToggleUserStatus(user.id, user.username, !!user.is_active, user)}
              className="deactivate-button"
              title="Deactivate User (temporary)"
              aria-label={`Deactivate user ${user.username}`}
              style={{
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
            >
              <FaUserTimes style={{ width: 20, height: 20 }} />
            </button>
          ) : (
            <button
              type="button"
              className="deactivate-button"
              disabled
              title="No permission to deactivate this user"
              aria-label={`Cannot deactivate user ${user.username}`}
              style={{
                opacity: 0.3,
                cursor: 'not-allowed',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FaUserTimes style={{ width: 20, height: 20 }} />
            </button>
          )}

          {canModifyUser(user) ? (
            <button
              type="button"
              onClick={() => handleDeleteUser(user.id, user.username, user)}
              className="delete-button"
              title="Delete User (permanent)"
              aria-label={`Delete user ${user.username}`}
            >
              <FaTrash style={{ width: 20, height: 20 }} />
            </button>
          ) : (
            <button
              type="button"
              className="delete-button"
              disabled
              title="No permission to delete this user"
              aria-label={`Cannot delete user ${user.username}`}
              style={{ opacity: 0.3, cursor: 'not-allowed' }}
            >
              <FaTrash style={{ width: 20, height: 20 }} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  const renderCreateModal = () =>
    showCreateModal && (
      <div className="modal-overlay fixed inset-0 z-50 d-flex align-items-center justify-content-center p-3">
        <div className="modal-content w-100" style={{ maxWidth: 560 }}>
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h3 className="m-0">Create New User</h3>
            <button
              onClick={() => {
                setShowCreateModal(false);
                resetNewUserForm();
              }}
              className="modal-close-button"
              aria-label="Close modal"
            >
              <FaTimes style={{ width: 20, height: 20 }} />
            </button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="mb-3 bg-danger-subtle border border-danger-subtle rounded p-2 small">
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                Username <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
                className="modal-input"
                placeholder="Enter username"
                autoComplete="off"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Email <span className="required-asterisk">*</span>
              </label>
              <input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                className="modal-input"
                placeholder="Enter email"
                autoComplete="off"
                required
              />
            </div>

            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label className="form-label">
                    First Name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUserData.first_name}
                    onChange={(e) => setNewUserData({ ...newUserData, first_name: capitalizeFirstLetter(e.target.value) })}
                    className="modal-input"
                    placeholder="First name"
                    autoComplete="off"
                    required
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label className="form-label">
                    Last Name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUserData.last_name}
                    onChange={(e) => setNewUserData({ ...newUserData, last_name: capitalizeFirstLetter(e.target.value) })}
                    className="modal-input"
                    placeholder="Last name"
                    autoComplete="off"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group mt-3">
              <label className="form-label">
                Role <span className="required-asterisk">*</span>
              </label>
              <select
                value={newUserData.role}
                onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                className="modal-select"
                required
              >
                <option value="field">Field Tech</option>
                <option value="admin">Office Admin</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Password <span className="required-asterisk">*</span>
              </label>
              <div className="password-input-container">
                <input
                  type={showNewPasswordInCreate ? 'text' : 'password'}
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  className="modal-input"
                  placeholder="Enter password (min. 6 characters)"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPasswordInCreate(!showNewPasswordInCreate)}
                  className="password-toggle-button"
                  aria-label={showNewPasswordInCreate ? 'Hide password' : 'Show password'}
                >
                  {showNewPasswordInCreate ? (
                    <FaEyeSlash style={{ width: 20, height: 20 }} />
                  ) : (
                    <FaEye style={{ width: 20, height: 20 }} />
                  )}
                </button>
              </div>
            </div>

            <div className="checkbox-container">
              <input
                type="checkbox"
                id="create_is_active"
                checked={newUserData.is_active}
                onChange={(e) => setNewUserData({ ...newUserData, is_active: e.target.checked })}
                className="checkbox-input"
              />
              <label htmlFor="create_is_active" className="checkbox-label">
                Active User
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                resetNewUserForm();
              }}
              className="cancel-button"
            >
              Cancel
            </button>
            <button type="button" onClick={handleCreateUser} className="save-button">
              <FaSave style={{ width: 16, height: 16 }} />
              Create User
            </button>
          </div>
        </div>
      </div>
    );

  const renderEditModal = () =>
    showEditModal &&
    selectedUser && (
      <div className="modal-overlay fixed inset-0 z-50 d-flex align-items-center justify-content-center p-3">
        <div className="modal-content w-100" style={{ maxWidth: 560 }}>
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h3 className="m-0">
              Edit User: <span className="fw-normal">{selectedUser.username}</span>
            </h3>
            <button onClick={() => setShowEditModal(false)} className="modal-close-button" aria-label="Close modal">
              <FaTimes style={{ width: 20, height: 20 }} />
            </button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="mb-3 bg-danger-subtle border border-danger-subtle rounded p-2 small">
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                Username <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                value={editUserData.username}
                onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
                className="modal-input"
                placeholder="Enter username"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Email <span className="required-asterisk">*</span>
              </label>
              <input
                type="email"
                value={editUserData.email}
                onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                className="modal-input"
                required
              />
            </div>

            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label className="form-label">
                    First Name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    value={editUserData.first_name}
                    onChange={(e) => setEditUserData({ ...editUserData, first_name: capitalizeFirstLetter(e.target.value) })}
                    className="modal-input"
                    required
                  />
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label className="form-label">
                    Last Name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    value={editUserData.last_name}
                    onChange={(e) => setEditUserData({ ...editUserData, last_name: capitalizeFirstLetter(e.target.value) })}
                    className="modal-input"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group mt-3">
              <label className="form-label">
                Role <span className="required-asterisk">*</span>
              </label>
              <select
                value={editUserData.role}
                onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                className="modal-select"
                required
              >
                <option value="field">Field Tech</option>
                <option value="admin">Office Admin</option>
              </select>
            </div>

            <div className="checkbox-container">
              <input
                type="checkbox"
                id="edit_is_active"
                checked={editUserData.is_active}
                onChange={(e) => setEditUserData({ ...editUserData, is_active: e.target.checked })}
                className="checkbox-input"
              />
              <label htmlFor="edit_is_active" className="checkbox-label">
                Active User
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={() => setShowEditModal(false)} className="cancel-button">
              Cancel
            </button>
            <button type="button" onClick={handleEditUser} className="save-button">
              <FaSave style={{ width: 16, height: 16 }} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );

  const renderPasswordModal = () =>
    showPasswordModal &&
    selectedUser && (
      <div className="modal-overlay fixed inset-0 z-50 d-flex align-items-center justify-content-center p-3">
        <div className="modal-content w-100" style={{ maxWidth: 560 }}>
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h3 className="m-0">
              Reset Password: <span className="fw-normal">{selectedUser.username}</span>
            </h3>
            <button
              onClick={() => setShowPasswordModal(false)}
              className="modal-close-button"
              aria-label="Close modal"
            >
              <FaTimes style={{ width: 20, height: 20 }} />
            </button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="mb-3 bg-danger-subtle border border-danger-subtle rounded p-2 small">
                {error}
              </div>
            )}

            {/* Hidden fake username field to catch autofill */}
            <input
              type="text"
              name="fake_username_field"
              tabIndex="-1"
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
              autoComplete="username"
              readOnly
            />

            <div className="form-group">
              <label className="form-label">
                New Password <span className="required-asterisk">*</span>
              </label>
              <div className="password-input-container">
                <input
                  type={showNewPasswordInReset ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="modal-input"
                  placeholder="Enter new password (min. 6 characters)"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPasswordInReset(!showNewPasswordInReset)}
                  className="password-toggle-button"
                  aria-label={showNewPasswordInReset ? 'Hide password' : 'Show password'}
                >
                  {showNewPasswordInReset ? (
                    <FaEyeSlash style={{ width: 20, height: 20 }} />
                  ) : (
                    <FaEye style={{ width: 20, height: 20 }} />
                  )}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Confirm Password <span className="required-asterisk">*</span>
              </label>
              <div className="password-input-container">
                <input
                  type={showConfirmPasswordInReset ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="modal-input"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPasswordInReset(!showConfirmPasswordInReset)}
                  className="password-toggle-button"
                  aria-label={showConfirmPasswordInReset ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPasswordInReset ? (
                    <FaEyeSlash style={{ width: 20, height: 20 }} />
                  ) : (
                    <FaEye style={{ width: 20, height: 20 }} />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={() => setShowPasswordModal(false)} className="cancel-button">
              Cancel
            </button>
            <button type="button" onClick={handleResetPassword} className="reset-password-button">
              <FaKey style={{ width: 16, height: 16 }} />
              Reset Password
            </button>
          </div>
        </div>
      </div>
    );

  const renderToggleModal = () =>
    showToggleModal &&
    toggleAction && (
      <div className="modal-overlay fixed inset-0 z-50 d-flex align-items-center justify-content-center p-3">
        <div className="modal-content w-100" style={{ maxWidth: 480 }}>
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h3 className="m-0 d-flex align-items-center gap-2">
              {toggleAction.newStatus ? (
                <>
                  <FaUserCheck style={{ width: 24, height: 24, color: '#10b981' }} />
                  <span>Activate User</span>
                </>
              ) : (
                <>
                  <FaUserTimes style={{ width: 24, height: 24, color: '#f59e0b' }} />
                  <span>Deactivate User</span>
                </>
              )}
            </h3>
            <button
              onClick={() => {
                setShowToggleModal(false);
                setToggleAction(null);
              }}
              className="modal-close-button"
              aria-label="Close modal"
            >
              <FaTimes style={{ width: 20, height: 20 }} />
            </button>
          </div>

          <div className="modal-body">
            <p className="mb-3">
              Are you sure you want to <strong>{toggleAction.newStatus ? 'activate' : 'deactivate'}</strong> user{' '}
              <strong>"{toggleAction.username}"</strong>?
            </p>

            {toggleAction.newStatus ? (
              <div
                className="alert mb-0"
                style={{
                  fontSize: '0.95rem',
                  backgroundColor: '#0ea5e9',
                  color: '#ffffff',
                  border: '1px solid #0284c7'
                }}
              >
                <strong>This will:</strong>
                <ul className="mb-0 mt-2" style={{ paddingLeft: '1.5rem' }}>
                  <li>Enable their account</li>
                  <li>Allow them to log in again</li>
                  <li>Restore their access to the system</li>
                </ul>
              </div>
            ) : (
              <div
                className="alert mb-0"
                style={{
                  fontSize: '0.95rem',
                  backgroundColor: '#f59e0b',
                  color: '#ffffff',
                  border: '1px solid #d97706'
                }}
              >
                <strong>This will:</strong>
                <ul className="mb-0 mt-2" style={{ paddingLeft: '1.5rem' }}>
                  <li>Disable their account temporarily</li>
                  <li>Prevent them from logging in</li>
                  <li>Keep their data in the system</li>
                  <li>Can be reversed by reactivating later</li>
                </ul>
                <div className="mt-2 small" style={{ opacity: 0.95 }}>
                  <strong>Note:</strong> This does NOT permanently delete the user. To permanently delete, use the Delete button.
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={() => {
                setShowToggleModal(false);
                setToggleAction(null);
              }}
              className="cancel-button"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmToggleUserStatus}
              className={toggleAction.newStatus ? 'save-button' : 'deactivate-button'}
              style={
                toggleAction.newStatus
                  ? {}
                  : {
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
                    }
              }
              onMouseEnter={(e) => {
                if (!toggleAction.newStatus) {
                  e.currentTarget.style.backgroundColor = '#ea580c';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(245, 158, 11, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!toggleAction.newStatus) {
                  e.currentTarget.style.backgroundColor = '#f59e0b';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(245, 158, 11, 0.2)';
                }
              }}
            >
              {toggleAction.newStatus ? (
                <>
                  <FaUserCheck style={{ width: 16, height: 16 }} />
                  Activate User
                </>
              ) : (
                <>
                  <FaUserTimes style={{ width: 16, height: 16 }} />
                  Deactivate User
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );

  /* ==================== MAIN RENDER ==================== */

  if (loading && !users.length) {
    return (
      <div className="user-management-page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span className="loading-text">Loading Users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page-container">
      {/* Page Header */}
      <div className="page-header">
        <h1>User Management</h1>
        <p>Manage user accounts, roles, and permissions</p>
      </div>

      <div className="container-fluid px-3">
        {renderAlerts()}

        {/* Controls */}
        <div className="controls-section d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="controls-left d-flex flex-wrap gap-3 flex-grow-1">
            <div className="search-container">
              <FaSearch className="search-icon" style={{ width: 18, height: 18 }} />
              <input
                type="search"
                name="search_query_users"
                id="user-search-input"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-form-type="other"
                data-lpignore="true"
                role="searchbox"
              />
            </div>

            <div className="filter-container">
              <FaFilter className="filter-icon" style={{ width: 18, height: 18 }} />
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="filter-select">
                <option value="all">All Roles</option>
                <option value="admin">Office Admin</option>
                <option value="field">Field Tech</option>
              </select>
            </div>
          </div>

          <div className="controls-right d-flex gap-2">
            <button
              onClick={handleDownloadScript}
              className="download-script-button"
              title="Download Excel automation script for Mac"
              type="button"
            >
              <FaDownload style={{ width: 18, height: 18 }} />
              Download Script
            </button>

            <button onClick={() => {
              resetNewUserForm();
              setShowCreateModal(true);
            }} className="add-user-button" type="button">
              <FaPlus style={{ width: 18, height: 18 }} />
              Add New User
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th className="actions-col" style={{ textAlign: 'center', verticalAlign: 'middle' }}>Actions</th>
              </tr>
            </thead>

            <tbody>{filteredUsers.map(renderUserRow)}</tbody>
          </table>

          {/* Empty state */}
          {filteredUsers.length === 0 && !loading && (
            <div className="no-users-message text-center p-4">
              <FaUsers style={{ width: 80, height: 80, opacity: 0.6 }} />
              <h3 className="mt-3 mb-2">No Users Found</h3>
              <p className="mb-3">
                {searchTerm || roleFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first user.'}
              </p>

              {!(searchTerm || roleFilter !== 'all') && (
                <button onClick={() => {
                  resetNewUserForm();
                  setShowCreateModal(true);
                }} className="add-user-button" type="button">
                  <FaPlus style={{ width: 18, height: 18 }} />
                  Add New User
                </button>
              )}
            </div>
          )}

          {/* Soft loading when refreshing */}
          {loading && users.length > 0 && (
            <div className="text-center py-3 small text-muted">Refreshing user list...</div>
          )}
        </div>
      </div>

      {/* Modals */}
      {renderCreateModal()}
      {renderEditModal()}
      {renderPasswordModal()}
      {renderToggleModal()}
    </div>
  );
};

export default UserManagementPage;